import clsx from 'clsx'
import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import AngleRightIcon from '@Assets/SVG/AngleRight'
import { SVGIcon } from '@Components/Icons'
import { up } from '@Globals/Style/breakpoints'
import { linkStyling } from '@Globals/Style/mixins'

export type AccordionStyleProps = Pick<AccordionProps, 'color'> & { open?: boolean }

export type SetOpen = (value: boolean) => void

export type AccordionProps = {
  children: ReactNode
  className?: string
  color?: 'white' | 'default'
  handleOnClick?: () => void
  header: ReactNode
  id?: string // Marked as optional to avoid destroying current implementation, but you REALLY should set this!
  open?: boolean
  subheader?: string
  iconRenderer?(props: AccordionStyleProps): JSX.Element
}

export const AccordionTitleWrapper = styled.span<AccordionStyleProps>(({ color }) => `
  order: ${color === 'white' ? '1' : '2'};
  display: flex;
  width: 100%;
  justify-content: space-between;
  text-transform: uppercase;

  > * {
    display: block;
    flex: 1 1 auto;
    text-align: left;
  }
`)

export const AccordionHeaderWrapper = styled.button<AccordionStyleProps>(({ color, theme }) => `
  display: flex;
  width: 100%;
  align-items: center;
  padding: 10px 15px;
  background-color: ${color === 'white' ? theme.colors.white : theme.colors.greyLightest};
  cursor: pointer;
  border: none;

  @media (hover: hover) {
    &:hover{
      ${AccordionTitleWrapper} {
        text-decoration: underline;
      }
    }
  }

  &[open] {
    ${AccordionTitleWrapper} {
      text-decoration: underline;
    }
  }

  ${up('mobileStyleThreshold')} {
    padding: 10px 15px 10px 15px;
  }
`)

export const AccordionTitle = styled.h3``

export const AccordionSubtitle = styled.span`
  padding-left: 1.5em;
  text-align: right;
  font-size: .875em;
  line-height: 1.5rem;
  text-transform: none;
`

export const AccordionIconWrap = styled.span<AccordionStyleProps>`
  margin-right: ${({ color }) => (color === 'white' ? '0' : '15px')};
  margin-left: ${({ color }) => (color === 'white' ? '15px' : '0')};
  transform: ${({ open }) => (open ? 'rotate(-90deg)' : 'rotate(90deg)')};
  transition: transform 75ms ease;
  order: ${({ color }) => (color === 'white' ? '2' : '1')};

  svg {
    fill: ${({ color, theme }) => (color === 'white' ? 'inherit' : theme.colors.primary)};
  }
`

export const AccordionWrapper = styled.section<AccordionStyleProps>`
  ${linkStyling};
  margin-bottom: ${({ open }) => (open ? '30px' : '15px')};

  &.whiteAccordion {
    & + & {
      margin-top: ${({ color }) => (color === 'white' ? '-15px' : '0')};
    }
  }
`

export const AccordionContents = styled.div<AccordionStyleProps>(({ color, theme }) => css`
  margin: 10px ${color === 'white' ? '15px' : '25px'};

  ${color !== 'white' ? `border-left: 1px solid ${theme.colors.grey};` : ''};

  p:last-child {
    padding: 0;
  }
`)

export const Accordion: FC<AccordionProps> = ({
  children,
  className,
  color,
  handleOnClick,
  header,
  iconRenderer,
  id,
  open = false,
  subheader,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(open)

  useEffect(() => {
    if (open !== isOpen) {
      setIsOpen(open)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const renderProps: AccordionStyleProps = {
    color,
    open: isOpen,
  }

  const buttonID = id && `${id}-btn`

  const innerHandleOnClick = useCallback(() => {
    setIsOpen(state => !state)
  }, [setIsOpen])

  const onClick = handleOnClick || innerHandleOnClick

  return (
    <AccordionWrapper
      {...renderProps}
      className={clsx({ whiteAccordion: color === 'white' }, className)}
    >
      <AccordionHeaderWrapper
        aria-expanded={open}
        id={buttonID}
        onClick={onClick}
        type="button"
        {...renderProps}
      >
        {iconRenderer
          ? iconRenderer(renderProps)
          : (
            <AccordionIconWrap aria-hidden {...renderProps}>
              <SVGIcon icon={AngleRightIcon} size="12px" />
            </AccordionIconWrap>
          )}
        <AccordionTitleWrapper {...renderProps}>
          <AccordionTitle>{header}</AccordionTitle>
          {subheader && <AccordionSubtitle aria-hidden>{subheader}</AccordionSubtitle>}
        </AccordionTitleWrapper>
      </AccordionHeaderWrapper>
      <AccordionContents
        aria-labelledby={buttonID}
        hidden={!isOpen}
        id={id}
        role="region"
        {...renderProps}
      >
        {children}
      </AccordionContents>
    </AccordionWrapper>
  )
}
